import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, Box, Chip, CircularProgress, IconButton, Stack } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledButton } from '../styles/StyledButtons';
import StyledSection from '../styles/StyledSection';
import { StyledCard, StyledCardContent, StyledHeading, StyledTypography } from '../styles/styles';
import ReviewComments from './ReviewComments';
import ReviewStatusActions from './ReviewStatusActions';
import ReviewViewerContentViews from './ReviewViewerContentViews';
import ReviewViewerHeader from './ReviewViewerHeader';
import ReviewViewerSaveChangesCard from './ReviewViewerSaveChangesCard';
import { shouldShowSaveAcceptedChangesButton } from './reviewViewerUtils';
import { useReviewViewer } from './useReviewViewer';

interface ReviewViewerProps {
  tone?: keyof typeof Palette;
}

export default function ReviewViewer({ tone = 'tone1' }: ReviewViewerProps) {
  const {
    navigate,
    reviewRequest,
    comments,
    currentUser,
    publishedContent,
    draftContent,
    reviewData,
    loading,
    error,
    viewMode,
    setViewMode,
    saving,
    savingDraft,
    handleStatusUpdate,
    handleCommentSubmit,
    handleReviewDataUpdate,
    handleSaveAcceptedChangesToDraft,
    handlePublish,
  } = useReviewViewer();

  if (loading) {
    return (
      <StyledSection tone={tone}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </StyledSection>
    );
  }

  if (viewMode === 'comparison' && !currentUser) {
    return <div>Loading user data...</div>;
  }

  if (error || !reviewRequest || !publishedContent || !reviewData) {
    return (
      <StyledSection tone={tone}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Review request not found'}
        </Alert>
        <StyledButton onClick={() => navigate('/reviews')}>Back to Reviews</StyledButton>
      </StyledSection>
    );
  }

  const showSaveButton = shouldShowSaveAcceptedChangesButton(
    reviewRequest,
    currentUser,
    reviewData,
    draftContent,
  );

  return (
    <StyledSection tone={tone} noSidePaddingFrom="xs">
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate('/reviews')}>
          <ArrowBackIcon />
        </IconButton>
        <StyledHeading tone={tone}>Review: {reviewRequest.fundOpportunity.name}</StyledHeading>
        {saving && (
          <Chip label="Saving..." color="info" size="small" icon={<CircularProgress size={16} />} />
        )}
        {savingDraft && (
          <Chip
            label="Saving to Draft..."
            color="success"
            size="small"
            icon={<CircularProgress size={16} />}
          />
        )}
      </Stack>

      <ReviewViewerHeader tone={tone} reviewRequest={reviewRequest} />

      {draftContent && currentUser && (
        <ReviewViewerContentViews
          tone={tone}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          publishedContent={publishedContent}
          draftContent={draftContent}
          reviewData={reviewData}
          reviewRequest={reviewRequest}
          currentUser={currentUser}
          onReviewDataUpdate={handleReviewDataUpdate}
          onSaveDraft={handleSaveAcceptedChangesToDraft}
        />
      )}

      <StyledCard tone={tone} sx={{ mb: 3, mt: 3 }}>
        <StyledCardContent>
          <StyledTypography tone={tone} variant="h6" sx={{ mb: 2 }}>
            General Comments ({comments.length})
          </StyledTypography>
          <ReviewComments comments={comments} onCommentSubmit={handleCommentSubmit} tone={tone} />
        </StyledCardContent>
      </StyledCard>

      <ReviewStatusActions
        currentStatus={reviewRequest.status}
        onStatusUpdate={handleStatusUpdate}
        currentUser={
          currentUser ? { id: currentUser.id, name: currentUser.name || 'Unknown User' } : undefined
        }
        requestedBy={reviewRequest.requestedBy}
        fundOpportunityId={reviewRequest.fundOpportunity.id}
        onPublish={handlePublish}
        tone={tone}
        reviewData={reviewData}
      />

      {showSaveButton && (
        <ReviewViewerSaveChangesCard
          tone={tone}
          savingDraft={savingDraft}
          onSave={() => void handleSaveAcceptedChangesToDraft()}
        />
      )}
    </StyledSection>
  );
}
